/* eslint-disable react/jsx-props-no-spreading */
import React from 'react';

import Information from '@/components/Information';
import { FhirPedidoType } from '@/features/enterprise/models/types';
import { getSolicitacaoObrigatoriaMotivo } from '@/features/recursos/utils/solicitacaoObrigatoria';

import OptionIntegracao from '@/types/OptionIntegracao';
import SolicitacoesAPI from '@/features/prescricao-livre/services/SolicitacoesAPI';
import {
  appointmentDateTimeDisplay,
  appointmentServiceType,
} from '../../helpers';
import FormAgendamento from '../FormAgendamento';
import {
  Actions,
  CollpsableContent,
  Container,
  Header,
  HeaderTitle,
  HeaderSubTitle,
  EditButton,
  ExpandButton,
  EditedLabel,
  EditTools,
} from './styles';

interface BoxAgendamentoEditavelProps {
  option: OptionIntegracao;
  detailsOpened?: boolean;
  disabled: boolean;
  onAfterEdit?: () => void;
  onAfterEditCancel?: () => void;
}

const BoxAgendamentoEditavel: React.FC<BoxAgendamentoEditavelProps> = ({
  option,
  detailsOpened,
  disabled,
  onAfterEdit,
  onAfterEditCancel,
}) => {
  const subtitleRef = React.useRef<HTMLHeadingElement>(null);
  const editableContentRef = React.useRef<HTMLDivElement>(null);

  const [isOpenForEdit, setIsOpenForEdit] = React.useState(
    detailsOpened || false,
  );
  const [isSubtitleExpanded, setIsSubtitleExpanded] = React.useState(false);
  const [
    agendamentoEditavel,
    setAgendamentoEditavel,
  ] = React.useState<FhirPedidoType>(option.pedido);
  const [isSubtitleExpandable, setSubtitleExpandable] = React.useState(false);
  const [loadingConfirmation, setloadingConfirmation] = React.useState(false);
  const solicitacaoObrigatoriaMotivo = React.useMemo(
    () =>
      getSolicitacaoObrigatoriaMotivo(
        'agendamento',
        option.solicitacao_obrigatoria_motivo,
      ),
    [option.solicitacao_obrigatoria_motivo],
  );

  const Title = React.useMemo(() => {
    const appointment = (agendamentoEditavel as unknown) as fhir4.Appointment;
    const service = appointmentServiceType(appointment);
    const dateTime = appointmentDateTimeDisplay(appointment);

    if (isOpenForEdit) {
      return service;
    }

    return (
      <>
        <strong>{service}</strong> | {dateTime}
      </>
    );
  }, [agendamentoEditavel, isOpenForEdit]);

  const description = React.useMemo(() => {
    const appointment = (agendamentoEditavel as unknown) as fhir4.Appointment;
    return appointment.description || '';
  }, [agendamentoEditavel]);

  const edited = React.useMemo(() => !!agendamentoEditavel.reportedBoolean, [
    agendamentoEditavel.reportedBoolean,
  ]);

  const canExpandSubtitle = React.useMemo(
    () => description && isSubtitleExpandable && !isOpenForEdit,
    [description, isOpenForEdit, isSubtitleExpandable],
  );

  const onEditClick = React.useCallback(
    (event: React.MouseEvent<HTMLDivElement | HTMLButtonElement>) => {
      event.stopPropagation();

      setIsOpenForEdit(true);
    },
    [],
  );

  const toggleCollapse = React.useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();

      // If disabled, collapse should display the edit form
      if (disabled) {
        setIsOpenForEdit(prev => !prev);
      }

      setIsSubtitleExpanded(prev => !prev);
    },
    [disabled],
  );

  const onEditItem = React.useCallback(
    async (item: fhir4.FhirResource) => {
      try {
        setloadingConfirmation(true);
        const result = await SolicitacoesAPI.update({
          id: option.value.toString(),
          tipo: 'agendamento',
          resource: item,
          opts: {
            session_id: option.sessionId,
          },
        });

        if (result) {
          setIsOpenForEdit(false);
          setIsSubtitleExpanded(false);
          setAgendamentoEditavel(result.pedido as FhirPedidoType);
          if (onAfterEdit) {
            onAfterEdit();
          }
        }
      } finally {
        setloadingConfirmation(false);
      }
    },
    [option.sessionId, option.value, onAfterEdit],
  );

  const onEditCancel = React.useCallback(
    async (item: fhir4.DomainResource) => {
      setAgendamentoEditavel(item as FhirPedidoType);
      setIsOpenForEdit(false);
      setIsSubtitleExpanded(false);

      if (onAfterEditCancel) {
        onAfterEditCancel();
      }
    },
    [onAfterEditCancel],
  );

  React.useEffect(() => {
    if (subtitleRef.current) {
      setSubtitleExpandable(
        subtitleRef.current.scrollHeight > subtitleRef.current.clientHeight,
      );
    }
  }, []);

  return (
    <Container detailsOpen={isOpenForEdit || isSubtitleExpanded}>
      <Header>
        <HeaderTitle highlight={isOpenForEdit}>{Title}</HeaderTitle>

        <Actions>
          <EditTools>
            {edited && <EditedLabel>editado</EditedLabel>}
            {!disabled && (
              <EditButton onClick={onEditClick} disabled={isOpenForEdit} />
            )}
          </EditTools>

          <ExpandButton
            disabled={!(disabled || canExpandSubtitle)}
            collapsed={isSubtitleExpanded}
            onClick={toggleCollapse}
          />
        </Actions>
      </Header>

      {!isOpenForEdit && description && (
        <HeaderSubTitle
          ref={subtitleRef}
          highlight={isSubtitleExpanded || isOpenForEdit}
        >
          {description}
        </HeaderSubTitle>
      )}

      {option.solicitacao_obrigatoria && (
        <Information type="warning" style={{ margin: '12px 0 0' }}>
          {solicitacaoObrigatoriaMotivo}
        </Information>
      )}

      <CollpsableContent ref={editableContentRef} highlight={isOpenForEdit}>
        <FormAgendamento
          resource={(agendamentoEditavel as unknown) as fhir4.Appointment}
          onSubmit={onEditItem}
          onCancel={onEditCancel}
          loading={loadingConfirmation}
          readOnly={disabled}
        />
      </CollpsableContent>
    </Container>
  );
};

export default BoxAgendamentoEditavel;
